<div
  class="h-full relative grid grid-rows-[auto_1fr] text-white border-neutral-600 dark overflow-hidden scrollbar-dark"
>
  <div class="flex flex-col gap-4 border-b border-neutral-600 px-4 py-6">
    <div class="flex justify-between items-center">
      <h2 class="font-bold text-xl leading-8">Execution Plan</h2>
      @if (currentExecutionPlanExists()) {
        <button
          [attr.data-testid]="'delete-execution-plan-button'"
          (click)="onDeleteExecutionPlanClick()"
          class="flx-ds flx-ds-ai primary"
          icon="center"
          flxButton
          variant="text"
        >
          <i
            class="ph ph-trash-simple"
            flxIcon
          ></i>
        </button>
      }
    </div>
    <div class="text-xs text-neutral-400">
      This AI-generated list includes resources identified in the uploaded document. You can ask the
      agent to implement each of them.
    </div>
  </div>

  <div class="flex flex-col gap-1 p-4 overflow-y-auto">
    @for (category of executionPlan(); track category.name) {
      <!-- Category Header -->
      <div
        [class.finished]="category.state === ActionStates.Finished"
        class="rounded-lg p-2 mb-0.5 flex items-center text-xs category-header"
      >
        @if (category.state === ActionStates.Pending) {
          <i class="ph ph-circle-dashed text-neutral-500 mr-2"></i>
        } @else if (category.state === ActionStates.Finished) {
          <i class="ph ph-check text-green-300 mr-2"></i>
        }
        <span class="font-semibold category-name">{{ category.name }}</span>
      </div>

      @for (action of category.actions; track action.localId) {
        <!-- Execution plan action -->
        <div
          [class.executing]="action.state === ActionStates.Executing"
          [class.finished]="action.state === ActionStates.Finished"
          class="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-2 ml-4 rounded-lg px-2 pt-3 pb-2 execution-plan-action"
        >
          @if (action.state === ActionStates.Pending) {
            <!-- Dashed circle icon column -->
            <i class="ph ph-circle-dashed self-center text-xs text-neutral-500"></i>
          } @else if (action.state === ActionStates.Executing) {
            <i class="ph ph-circle-dashed self-center text-xs status-icon"></i>
          } @else if (action.state === ActionStates.Finished) {
            <i class="ph ph-check self-center text-xs text-green-300"></i>
          }

          <!-- Row with action title and new chip -->
          <div class="flex flex-row items-center justify-between self-center">
            <h3 class="text-sm font-bold action-name">{{ action.name }}</h3>
            <!-- TODO: uncomment when the enhance plan is implemented -->
            <!-- @if (action.state === ActionStates.Pending) {
              <span class="bg-blue-600 text-white text-2xs font-medium px-1 py-0.5 rounded"
                >New</span
              >
            } -->
          </div>

          <!-- Action contents column -->
          <div class="flex flex-col gap-1 col-start-2 min-w-0">
            <!-- Section with Destination and Source -->
            <div>
              <!-- Row with Destination -->
              <div class="flex flex-row items-center justify-between pl-2">
                <div class="flex items-center gap-1 min-w-0">
                  <span class="text-neutral-200 font-bold text-2xs">Destination:</span>
                  <div class="flex items-center gap-1 text-2xs destination min-w-0 overflow-hidden">
                    @if (action.uiTarget.hasDestination) {
                      <ng-template #businessRuleDestination>
                        <div
                          class="grid grid-cols-[minmax(0,35%)_auto_minmax(0,35%)_auto_minmax(0,1fr)] gap-1 items-center w-full"
                        >
                          <div class="text-ellipsis overflow-hidden whitespace-nowrap">
                            {{ action.uiTarget.nodeName }}
                          </div>
                          <div class="text-neutral-200">\</div>
                          <div class="text-ellipsis overflow-hidden whitespace-nowrap">
                            {{ action.uiTarget.actionName }}
                          </div>
                          <div class="text-neutral-200">\</div>
                          <div class="text-ellipsis overflow-hidden whitespace-nowrap">
                            Rule {{ action.target?.businessRule?.index + 1 }}
                          </div>
                        </div>
                      </ng-template>

                      @switch (action.agent.agent) {
                        @case (AgentName.AiAnalyst) {
                          <div class="text-ellipsis overflow-hidden whitespace-nowrap max-w-full">
                            {{ currentProcess()?.resourceName }}
                          </div>
                        }
                        @case (AgentName.AiDesigner) {
                          <div class="text-ellipsis overflow-hidden whitespace-nowrap max-w-full">
                            {{ action.uiTarget.nodeName }}
                          </div>
                        }
                        @case (AgentName.AiDeveloper) {
                          @switch (action.agent.action) {
                            @case (AgentAction.BusinessRule) {
                              @if (action.uiTarget.isComplete) {
                                <ng-container
                                  *ngTemplateOutlet="businessRuleDestination"
                                ></ng-container>
                              } @else {
                                <div
                                  class="text-ellipsis overflow-hidden whitespace-nowrap max-w-full"
                                >
                                  {{ action.uiTarget.nodeName }}
                                </div>
                              }
                            }
                            @case (AgentAction.EditBusinessRule) {
                              @if (action.uiTarget.isComplete) {
                                <ng-container
                                  *ngTemplateOutlet="businessRuleDestination"
                                ></ng-container>
                              } @else {
                                <div
                                  class="text-ellipsis overflow-hidden whitespace-nowrap max-w-full"
                                >
                                  {{ action.uiTarget.nodeName }}
                                </div>
                              }
                            }
                            @case (AgentAction.Expression) {
                              @if (action.uiTarget.isComplete && templateConfigs) {
                                <div
                                  class="grid grid-cols-[minmax(0,60%)_auto_minmax(0,60%)] gap-1 items-center w-full"
                                >
                                  <div class="text-ellipsis overflow-hidden whitespace-nowrap">
                                    {{ action.uiTarget.nodeName }}
                                  </div>
                                  <div class="text-neutral-200">\</div>
                                  <div class="text-ellipsis overflow-hidden whitespace-nowrap">
                                    {{ action.uiTarget.elementName }}
                                  </div>
                                </div>
                              } @else {
                                <div
                                  class="text-ellipsis overflow-hidden whitespace-nowrap max-w-full"
                                >
                                  {{ action.uiTarget.nodeName }}
                                </div>
                              }
                            }
                          }
                        }
                      }
                    } @else {
                      <span [class.text-orange-500]="!action.target">'Not set' </span>
                    }
                  </div>
                </div>
                @if (
                  action.uiTarget.hasDestination &&
                  action.state === ActionStates.Pending &&
                  action.agent.agent !== AgentName.AiAnalyst
                ) {
                  <button
                    (click)="onClearDestinationClick(action)"
                    class="flex items-center justify-center !h-3 !w-3 p-0 disabled:opacity-50 flex-shrink-0"
                    variant="icon"
                  >
                    <i class="ph ph-x text-xs text-yellow-500"></i>
                  </button>
                }
                <!-- TODO: undecided if we'll use this popup -->
                <!-- @if (action.state === ActionStates.Pending) {
                  <div class="relative dark">
                    <button
                      (click)="onEditDestinationClick(action)"
                      class="flex items-center justify-center !h-3 !w-3 p-0 disabled:opacity-50"
                      variant="icon"
                    >
                      <i class="ph ph-gear text-xs text-yellow-500"></i>
                    </button>
                    <p-select
                      [attr.id]="'node-selector-' + action.localId"
                      [options]="destinationOptions()"
                      [overlayOptions]="{ styleClass: 'scale-100' }"
                      [filter]="true"
                      (onChange)="onDestinationChange(action, $event)"
                      [(ngModel)]="action.target"
                      class="dark"
                      optionLabel="label"
                      optionValue="id"
                      styleClass="hidden-label"
                      appendTo="body"
                      filterBy="name"
                      panelStyleClass="flx-prime-select small dark dark-panel"
                    >
                      <ng-template pTemplate="empty">
                        <div class="text-center text-neutral-400 text-xs">
                          <div>No nodes found Create one on the canvas</div>
                          <div>and select it here</div>
                        </div>
                      </ng-template>

                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        <div class="flex flex-row items-center gap-2">
                          <i class="ph ph-user"></i>
                          <span class="text-xs">{{ option.label }}</span>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>
                } -->
              </div>

              <!-- Row with Source -->
              <div class="flex flex-row items-center justify-between pl-2">
                <div class="flex items-center gap-1 text-2xs min-w-0">
                  <span class="text-neutral-200 font-bold">Source:</span>
                  <span class="flex items-center gap-1 source min-w-0">
                    @if (getActionFileName(action); as fileName) {
                      <i class="ph ph-file-text"></i>
                      <span class="truncate">{{ fileName }}</span>
                    } @else {
                      User prompt
                    }
                  </span>
                </div>
                @if (
                  action.agent.agent === AgentName.AiAnalyst ||
                  action.agent.agent === AgentName.AiDesigner
                ) {
                  <button
                    [hidden]="action.state === ActionStates.Finished"
                    [disabled]="action.state === ActionStates.Executing"
                    (click)="toggleActionSource(action)"
                    class="flex items-center justify-center !h-3 !w-3 p-0 disabled:opacity-50"
                    variant="icon"
                  >
                    <i
                      [ngClass]="{ 'ph-gear': !action.hasSource, 'ph-x': action.hasSource }"
                      class="ph text-xs text-yellow-500"
                    ></i>
                  </button>
                }
              </div>
            </div>

            <!-- Row with buttons -->
            <div class="flex flex-row items-center justify-between mt-1">
              <div class="flex items-center gap-2">
                <button
                  [hidden]="action.state === ActionStates.Finished"
                  [disabled]="isAnyActionExecuting()"
                  [attr.data-testid]="
                    'implement-button-' + category.name.toLowerCase().replace(' ', '-')
                  "
                  (click)="onImplementClick(action)"
                  class="text-yellow-500 bg-yellow-900 enabled:hover:bg-yellow-800 text-white px-2 py-1.5 flex items-center gap-1.5 rounded-lg text-xs disabled:opacity-50"
                  flxButton
                  type="button"
                >
                  <i class="ph ph-sparkle"></i>
                  Implement
                </button>
                <!-- TODO: uncomment when the feature is implemented -->
                <!-- <button
                  [hidden]="action.state === ActionStates.Finished"
                  [disabled]="action.state === ActionStates.Executing"
                  class="rounded-md bg-[#2a3441] enabled:hover:bg-[#3a4451] h-7 w-7 disabled:opacity-50"
                  flxButton
                  icon="center"
                >
                  <i
                    class="ph ph-dots-three-vertical text-xs text-white"
                    flxIcon
                  ></i>
                </button> -->
              </div>
              <button
                [hidden]="action.state === ActionStates.Finished"
                (click)="toggleDetails(action.localId)"
                class="text-[#d4a853] hover:text-[#e4b863] font-semibold text-2xs px-0"
                flxButton
                type="button"
                variant="text"
              >
                {{ expandedActions.has(action.localId) ? 'Hide Details' : 'Show Details' }}
              </button>
            </div>

            <!-- Collapsible description section -->
            @if (expandedActions.has(action.localId)) {
              <p class="text-neutral-200 text-2xs leading-relaxed">
                {{ action.description }}
              </p>
            }
          </div>
        </div>
      }
    }
  </div>

  @if (showEnhancingPlanLoader()) {
    <div
      class="absolute flex flex-col justify-center bg-neutral-700/20 items-center top-0 bottom-0 left-0 right-0 z-1"
    >
      <div class="flex flex-col gap-3.5 bg-neutral-700 p-7 rounded-lg items-center justify-center">
        <flx-spinner
          [radius]="17"
          [border]="3"
          class="!relative !w-full !h-full !bg-transparent"
          backgroundColor="#8390A2"
          borderColor="#FEB913"
          size="custom"
        />
        <p class="text-xs font-semibold leading-normal">Enhancing plan...</p>
      </div>
    </div>
  }
</div>
